import React, { useState, useEffect } from "react";
import { connect, useDispatch } from "react-redux";
import { RouteComponentProps } from "react-router";
import {
  IonButton,
  IonCheckbox,
  IonContent,
  IonToggle,
  IonItemGroup,
  IonPage,
  IonInput,
  IonItem,
  IonLabel,
  useIonViewWillEnter,
  IonIcon,
  IonAlert,
  isPlatform,
} from "@ionic/react";

import "./login.scss";
import logoLight from "../../assets/images/logo/nserix-primary.svg";
import logoDark from "../../assets/images/logo/nserix-dark.svg";
import { loginPageService } from "./login.service";
import { LoginActions } from "./../../redux/actions";
import { CONSTANTS, ERROR } from "../../constants";
import { loggedInResultInterface, NetworkResult } from "./login.model";
import { Footer } from "../../components/Shared/Footer/Footer";
import Loader from "../../components/Shared/Loader/Loader";
import TermsAndPrivacy from "../../components/Shared/TremsAndPrivacy/TermsAndPrivacy";
import { useLocation } from "react-router-dom";
import { AuthService } from "../../services/authService";
import _ from "lodash";
import { settingsService } from "../MenuSettings/components/Settings/Settings.service";
import { moon, sunny } from "ionicons/icons";
import { Capacitor } from "@capacitor/core";
import { StatusBar, Style } from "@capacitor/status-bar";
import { useTranslation } from "react-i18next";
import i18n from "../../locales/i18n/i18n";
import { isSupportedLanguage } from "../../locales/i18n/languages";

interface OwnProps extends RouteComponentProps { }
interface LoginProps extends OwnProps { }

const LoginPage: React.FC<LoginProps> = ({ history }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation("login");

  const [inputs, setInputs] = useState({
    username: "",
    password: "",
    network: "",
  });

  const userHomeURL = "/client/home";
  const emtHomeURL = "/client/emt-home";

  const [type, setModalType] = useState<"terms" | "privacy">("terms");
  const [submitted, setSubmitted] = useState(false);
  const [loginStatus, setLoginStatus] = useState("");
  const [isFormValid, setFormStatus] = useState(false);
  const [networkStatus, setNetworkStatus] = useState("");
  const [isSavePassword, setChecked] = useState(true);
  const [isLoaderActive, setLoaderStatus] = useState(false);
  const [isShowPassword, setPasswordStatus] = useState(false);
  const [isShowModalOpen, setShowModalStatus] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isShowAlert, setIsShowAlert] = useState(false);

  const location = useLocation();
  var { username, password, network } = inputs;

  useIonViewWillEnter(async () => {
    AuthService.isAuthenticated();
    const authenticated = await AuthService.isAuthenticated();
    if (authenticated) {
      window.location.href = "/client/home";
    }
  });

  //proceed to mobile application
  useEffect(() => {
    if (isPlatform("mobileweb")) {
      setIsShowAlert(true);
    }
  }, []);

  useEffect(() => {
    const savedLang = localStorage.getItem("appLanguage");

    // Only re-apply a language we actually ship. i18n.ts validates the value it
    // initialises with, but this call would otherwise override that guard and
    // hand i18next a stale code, leaving the login form with unresolved keys.
    if (savedLang && isSupportedLanguage(savedLang)) {
      i18n.changeLanguage(savedLang);
    }
  }, []);

  useEffect(() => {
    const storedTheme = localStorage.getItem("isDarkMode");

    if (storedTheme !== null) {
      setIsDarkMode(storedTheme === "true");
      document.body.className =
        storedTheme === "true" ? "dark-theme" : "light-theme";
    } else {
      setIsDarkMode(true);
      document.body.className = "dark-theme";
      settingsService.themeSwitch = true;

      if (Capacitor.getPlatform() === "ios") {
        if (storedTheme === null || storedTheme === true) {
          StatusBar.setStyle({ style: Style.Dark });
        } else {
          StatusBar.setStyle({ style: Style.Light });
        }
      }
    }
  }, []);

  useEffect(() => {
    if (!_.isEmpty(location.state) && location.pathname === "/login") {
      const currentLocation: any = location;
      if (currentLocation.state.networkCodeDetails) {
        const name = "network";
        const value = currentLocation.state.networkCodeDetails;
        setInputs((inputs) => ({ ...inputs, [name]: value }));
        setLoginStatus("");
        setNetworkStatus("");
      }
    }
  }, [location]);

  useEffect(() => {
    setFormStatus(formValidation());
  }, [username, password, network]); // any state variable(s) included in here will trigger the effect to run

  const formValidation = () => {
    return username && password && network ? true : false;
  };


  // useEffect(() => {

  //   getSIpCall()
  // }, []);

  // const getSIpCall = async () =>{
  //     await getSipCredentials();
  // }

  const getSipCredentials = async () => {
    try {
      const sipData: any = await loginPageService.getSipCredentials({
        reg_code: network, // ✅ IMPORTANT
      });


      if (sipData?.isSuccess) {
        localStorage.setItem("sipCred", JSON.stringify(sipData?.result));
      }
    } catch (err) {
      console.error("Failed to fetch SIP credentials", err);
    }
  };


  const handleChange = (e: any) => {
    const { name, value } = e.target;
    setInputs((inputs) => ({ ...inputs, [name]: value.trim() }));
    setLoginStatus("");
    setNetworkStatus("");
  };

  const toggleDarkModeHandler = (status: boolean) => {
    if (Capacitor.getPlatform() === "ios") {
      if (status === true) {
        StatusBar.setStyle({ style: Style.Dark });
      } else {
        StatusBar.setStyle({ style: Style.Light });
      }
    }
    localStorage.setItem("isDarkMode", status.toString());

    document.body.className = status ? "dark-theme" : "light-theme";
    settingsService.themeSwitch = status;
    setIsDarkMode(status);
  };

  const onShowPassword = () => {
    setPasswordStatus(!isShowPassword);
  };

  async function onLoginSubmit(e: any) {

    e.preventDefault();

    setSubmitted(true);
    setLoaderStatus(true);
    const networkStatus = await checkNetwork();
    if (username && password && network && networkStatus) {
      let params = loginPageService.getLoginParams(inputs);

      const respResult: loggedInResultInterface = await loginPageService.login(
        params
      );
      if (respResult?.isSuccess && respResult?.loggedInUser) {
        const rolePermissions = loginPageService.setRolePermissionModel(
          respResult.loggedInUser?.rolePermissions ?? ""
        );


        dispatch(LoginActions.loggedInUserData(respResult.loggedInUser));
        dispatch(LoginActions.loggedInUserPermission(rolePermissions));
        // Anything thrown while writing the session to storage used to escape
        // this handler, so setLoaderStatus(false) below never ran and the
        // spinner span forever after a successful sign-in.
        try {
        loginPageService.setStorage(
          respResult?.loggedInUser!.id,
          isSavePassword,
          respResult?.loggedInUser!.token,
          respResult?.loggedInUser!.guid,
          network,
          rolePermissions,
          respResult?.loggedInUser!.role,
          isDarkMode,
          respResult?.loggedInUser!.unavailableWhenNotoncall,
          respResult?.loggedInUser!.n2nActivationStatus,
          respResult?.loggedInUser!.firstName,
          respResult?.loggedInUser!.lastName,
          respResult?.loggedInUser!.n2nPermission,
          respResult?.loggedInUser!.sipUsername,
        );

        } catch (error) {
          console.error("Could not store the session:", error);
          setLoginStatus(ERROR.LoginErrorMessages.LOGIN_ERROR);
          setLoaderStatus(false);
          return;
        }

        await getSipCredentials();
        if (checkIsEMTUser(respResult.loggedInUser)) {
          window.location.href = emtHomeURL;
        } else {
          window.location.href = userHomeURL;
        }
      } else {
        console.log("Login failed response:", respResult);

        loginPageService.handleErrorResponse();

        // ✅ show API message safely
        const errorMsg =
          (respResult as any)?.message ||
          (respResult as any)?.error ||
          ERROR.LoginErrorMessages.LOGIN_ERROR;

        setLoginStatus(errorMsg);
      }
    }
    setLoaderStatus(false);
  }

  const checkIsEMTUser = (loggedInUser: any): any => {
    let emtRole = CONSTANTS.ROLES.EMT;
    if (loggedInUser) {
      return loggedInUser.role.some(function (item: any) {
        return item.name === emtRole;
      });
    }
  };

  const checkNetwork = async () => {
    var status = false;
    const respResult: NetworkResult = await loginPageService.checkNetwork({
      reg_code: network,
    });

    if (respResult.isSuccess) {
      if (respResult.isValidNetwork) {
        status = true;
      } else {
        setNetworkStatus(ERROR.LoginErrorMessages.NETWORK_ERROR);
      }
    }

    return status;
  };

  const openTermsPrivacyModal = (modalType: "terms" | "privacy") => {
    setModalType(modalType);
    setShowModalStatus(true);
  };

  const handleKeypress = (e: any) => {
    if (e.key === "Enter") {
      onLoginSubmit(e);
    }
  };

  /**
   * "Open in the app" from the browser.
   *
   * Every destination this used to have was the previous vendor's: a
   * com.ccl.callcentral deep link, the Play and App Store pages for *their*
   * app, and customer.<vendor> for desktop. None is reachable from a hospital
   * network and none is this product, so they are gone from this branch
   * rather than left behind a runtime flag. Here the browser is the app.
   */
  const proceedToApp = () => {};

  return (
    <IonPage>
      <IonContent className="ion-padding">
        <div className="login-container">
          <div className="login-input-container">
            <div className="callcentral-logo-container">
              <div className="logo-wrapper">
                <img
                  className="callcentral-logo"
                  src={isDarkMode ? logoDark : logoLight}
                  alt="Nserix"
                />

              </div>
              <div
                className={`toggle-button-wrapper ${isDarkMode ? "toggle-dark-mode" : ""
                  }`}
              >
                {isDarkMode && (
                  <div className="toggle-image">
                    <IonIcon slot="start" icon={sunny} />
                  </div>
                )}
                <IonToggle
                  className="theme-toggle-button"
                  checked={isDarkMode}
                  title="theme-switcher"
                  onIonChange={(e: any) => {
                    toggleDarkModeHandler(e.detail.checked);
                  }}
                ></IonToggle>
                {!isDarkMode && (
                  <div className="toggle-image">
                    <IonIcon slot="start" icon={moon} />
                  </div>
                )}
              </div>
            </div>

            <div className="login-input-wrapper">
              {isLoaderActive && (
                <div className="loader-container">
                  <Loader></Loader>
                </div>
              )}
              <div className="login-input-header">
                <div className="welcome-wrapper">
                  <span className="welcome">{t("welcome", "Welcome!")}</span>
                </div>
                <br></br>
                <span className="header-title">
                  {t("signInToContinue", "Sign in to continue to Nserix")}
                </span>
              </div>
              <form
                name="form"
                onSubmit={onLoginSubmit}
                onKeyPress={(e) => {
                  handleKeypress(e);
                }}
              >
                <div className="item-input-wrapper">
                  <div className="input-icon icon-name"></div>
                  <IonItem className="item-input">
                    <IonLabel className="item-label" position="floating">
                      {t("username")}
                    </IonLabel>
                    <IonInput
                      className="ion-input"
                      name="username"
                      value={username}
                      onIonChange={handleChange}
                    >
                      {" "}
                    </IonInput>
                  </IonItem>
                  {submitted && !username && (
                    <div className="invalid-feedback">{t("usernameRequired")}</div>
                  )}
                </div>
                <div className="item-input-wrapper">
                  <div
                    className={`input-icon clickable ${isShowPassword
                        ? "icon-show_password"
                        : "icon-hide_password"
                      }`}
                    onClick={onShowPassword}
                  ></div>
                  <IonItem className="item-input">
                    <IonLabel className="item-label" position="floating">
                      {t("password")}
                    </IonLabel>
                    <IonInput
                      className="ion-input"
                      type={isShowPassword ? "text" : "password"}
                      name="password"
                      value={password}
                      onIonChange={handleChange}
                    >
                      {" "}
                    </IonInput>
                  </IonItem>
                  {submitted && !password && (
                    <div className="invalid-feedback">{t("passwordRequired")}</div>
                  )}
                </div>
                <div className="item-input-wrapper">
                  <div className="input-icon icon-network"></div>
                  <IonItem className="item-input">
                    <IonLabel className="item-label" position="floating">
                      {t("network")}
                    </IonLabel>
                    <IonInput
                      className="ion-input"
                      name="network"
                      value={network}
                      onIonChange={handleChange}
                    >
                      {" "}
                    </IonInput>
                  </IonItem>
                  {submitted && !network && (
                    <div className="invalid-feedback">{t("networkRequired")}</div>
                  )}
                </div>

                <div className="messageStatus">
                  {networkStatus && t("networkError", networkStatus)}
                  {loginStatus && t("loginError", loginStatus)}
                </div>

                <div className="password-wrapper">
                  <IonItemGroup>
                    <div className="save-password">
                      <IonItem>
                        <IonCheckbox
                          checked={isSavePassword}
                          onIonChange={(e) => setChecked(e.detail.checked)}
                        />
                        <IonLabel>{t("savePassword")}</IonLabel>
                      </IonItem>
                    </div>

                    <div
                      className="forgot-password"
                      onClick={() => {
                        history.push({
                          pathname: "/forgot-password",
                          state: { buttonDetails: "forgot-password" },
                        });
                      }}
                    >
                      <span>{t("forgotPassword")}</span>
                    </div>
                    <div
                      className="forgot-username"
                      onClick={() => {
                        history.push({
                          pathname: "/forgot-password",
                          state: { buttonDetails: "forgot-username" },
                        });
                      }}
                    >
                      <span>{t("forgotUsername")}</span>
                    </div>
                  </IonItemGroup>
                </div>
                <div>
                  <IonButton
                    className="button primary-btn login-btn btn-curved"
                    onClick={(e) => {
                      onLoginSubmit(e);
                    }}
                    disabled={!isFormValid}
                  >
                    {t("signIn")}
                  </IonButton>
                </div>

                <div className="link-container">
                  <div className="link-wrapper">
                    <div className="terms-privacy">
                      <span
                        className="terms clickable"
                        onClick={() => openTermsPrivacyModal("terms")}
                      >
                        {t("terms")}
                      </span>
                      <span
                        className="privacy clickable"
                        onClick={() => openTermsPrivacyModal("privacy")}
                      >
                        {t("privacy")}
                      </span>
                      {isShowModalOpen ? (
                        <TermsAndPrivacy
                          onOk={() => {
                            setShowModalStatus(false);
                          }}
                          showModal={isShowModalOpen}
                          page={"login"}
                          type={type}
                        />
                      ) : (
                        ""
                      )}
                    </div>
                    <div className="footer-container">
                      <Footer />
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
          <div className="loginbg-container">
            <div className="bgimage-text-wrapper">
              <span className="white-text">
                {t("oneStopSolution")}<br></br>
              </span>
              <span className="white-text">
                {t("platformIndependent")} <br></br>
              </span>
              <span className="green-text">
                {t("doctorManagement")}<br></br>
              </span>
            </div>
          </div>
          <IonAlert
            isOpen={isShowAlert}
            onDidDismiss={() => setIsShowAlert(false)}
            cssClass="proceed-to-app-alert"
            header={t("confirmation")}
            message={t("proceedToApp", CONSTANTS.MESSAGES.PROCEED_TO_APP)}
            buttons={[
              {
                text: t("cancel"),
                role: "cancel",
                cssClass: "secondary",
              },
              {
                text: t("proceed"),
                handler: () => {
                  proceedToApp();
                  setIsShowAlert(false);
                },
              },
            ]}
          />
        </div>
      </IonContent>
    </IonPage>
  );
};

const mapStateToProps = (state: any, ownProps: any) => ({
  loggedUser: state.loginReducer.loggedInUserData,
  rolePermission: state.loginReducer.loggedInUserPermission,
});

const mapDispatchToProps = (dispatch: any) => {
  return {};
};

export default connect(mapStateToProps, mapDispatchToProps)(LoginPage);
